import moment from "moment-timezone"
import { countMondays } from "@core/helpers"
import { formatEvolution } from "@core/helpers"

import { SourceLimit } from "@types"
import {
    DISPLAY_DATA_CONFIGURATION,
    FREQ_AVAILABLE_BY_VARIABLES,
} from "@modules/energy-manager/constants"

/**
 * Permet de récupérer la configuration pour l'affichage des données en fonction du nombre de sources
 * @param amount - Nombre de sources
 * @returns La configuration pour l'affichage des données
 */
export function getSourceConfigByAmount(amount: number) {
    return DISPLAY_DATA_CONFIGURATION.find(
        (config) => amount >= config.min && amount <= config.max
    )
}

/**
 * Permet de récupérer la première fréquence disponible pour une variable
 * @param variable - La variable
 * @returns La première fréquence disponible
 */
export function getMinFreqAvailableByVariable(variable: string) {
    // Trouve la première fréquence disponible
    const minFreq = FREQ_AVAILABLE_BY_VARIABLES.find(
        (config) => config.variable === variable
    )?.frequencyConfigs[0]

    return minFreq
}

/**
 * Indique si on peut aller en asynchrone pour une fréquence donnée
 * @param config - Configuration de la limite de période
 * @param period - Période sélectionnée
 * @param freq - Fréquence sélectionnée
 * @returns true si la période est inférieure ou égale à la période maximale, false sinon
 */
export function isGoAsyncByConfig(
    config: SourceLimit,
    period: [Date | null, Date | null],
    freq: string
) {
    const configAsync = config.config_periods[freq]?.async

    // Si la configuration n'est pas définie, on ne peut pas aller en asynchrone
    if (!configAsync) return false

    const diffDays = computedDiffDays(period, freq)

    // Vérifie que la période est inférieure ou égale à la période maximale
    switch (configAsync.unit) {
        case "week":
            return diffDays > configAsync?.quantity * 7
        case "month":
            return diffDays > configAsync?.quantity * 31
        case "year":
            return diffDays - 1 > configAsync?.quantity * 366
        default:
            return diffDays > configAsync?.quantity
    }
}

/**
 * Calcule le nombre de jours entre deux dates
 * @param period - Période sélectionnée
 * @param freq - Fréquence sélectionnée
 * @returns Nombre de jours entre deux dates
 */
export function computedDiffDays(
    period: [Date | null, Date | null],
    freq: string
) {
    if (period[0] && period[1]) {
        switch (freq) {
            case "week":
                // Calcul le nombre de semaine complète (du lundi au dimanche inclus) à partir
                // de la date de début et de fin

                return countMondays(period[0], period[1])
            case "month":
                return moment(period[1]).diff(moment(period[0]), "months") + 1
            case "year":
                return moment(period[1]).diff(moment(period[0]), "years") + 1
            default:
                return moment(period[1]).diff(moment(period[0]), "days") + 1
        }
    }
    return 0
}

/**
 * Fonction trouvant la période de l'année dernière qui correspond à la période actuelle - 1 an tout
 * en conservant le même nombre de lundis. Il y a donc un ajustement sur la fin de la période pour matcher le nombre de lundis.
 * @param referenceStart - Date de début de la période actuelle
 * @param referenceEnd - Date de fin de la période actuelle
 * @returns - [Date, Date] - Période de l'année dernière correspondante au même nombre de lundis
 */
export function findMatchingLastYearWeekPeriod(
    referenceStart: Date,
    referenceEnd: Date
) {
    // Recule d'un an
    const newStart = moment(referenceStart).subtract(1, "year")
    const newEnd = moment(referenceEnd).subtract(1, "year")

    const targetMondays = countMondays(referenceStart, referenceEnd)
    let currentMondays = countMondays(newStart.toDate(), newEnd.toDate())

    // Ajuster la fin de la période pour matcher le nombre de lundis
    while (currentMondays !== targetMondays) {
        if (currentMondays < targetMondays) {
            newEnd.add(1, "days")
        } else {
            newEnd.subtract(1, "days")
        }
        currentMondays = countMondays(newStart.toDate(), newEnd.toDate())
    }

    return [newStart.toDate(), newEnd.toDate()]
}

/**
 * Fonction qui calcule l'évolution entre deux valeurs et la formate
 * @param firstData - Première donnée
 * @param secondData - Deuxième donnée
 * @param maxDigit - Nombre de chiffres après la virgule
 * @param unit - Unité de la variable
 *
 * @returns [number, string] - Evolution et évolution formatée
 */
export function computedAndFormatEvolution(
    firstData: number,
    secondData: number,
    {
        maxDigit,
        unit,
    }: {
        maxDigit: number
        unit: string
    } = {
        maxDigit: 1,
        unit: "",
    }
) {
    // si une des données est null on retourne null
    if (secondData === undefined || !firstData) return [null, null]

    const diff = firstData - secondData
    const evolution = diff / secondData

    // Si l'évolution est NaN ou infinie on retourne null
    if (isNaN(evolution) || !isFinite(evolution)) return [null, null]

    const evolutionFormated = formatEvolution(evolution, diff, {
        maxDigit,
        unit,
    })

    return [evolution, evolutionFormated]
}
